import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Shimmer from "../components/Shimmer.jsx";
import { useWallet } from "../context/WalletContext.jsx";
import { fetchTvtCurrent, fetchTvtSeries, fetchTvtZone } from "../services/api.js";
import { chainNowSec } from "../utils/chainClock.js";

/**
 * $GILD Token page.
 *
 * Per client feedback the My Tokens surface is NOT a deposit-NFT gallery
 * (the My Term Deposits page already owns that). It's the $GILD token
 * page: current spot + intrinsic value, a price-history chart, and a
 * Buy CTA. The chart sources `treasuryValuePerToken` from the backend's
 * snapshot loop (`/api/tvt/series`) — TVT is the protocol-backed value
 * each $GILD is mathematically entitled to, drawn from the Treasury
 * balance ÷ circulating supply. Spot price is layered on top whenever
 * the snapshot captured a non-zero `marketPrice`; otherwise the chart
 * is a single TVT line.
 *
 * Real-time tail: the chart appends a synthetic "now" point computed
 * from `/api/tvt/current` + `/api/tvt/zone` polled every 5s, so the
 * far-right of the curve visibly ticks. A pulsing dot + LIVE badge
 * signal that the figure is live, not a stale snapshot.
 *
 * Trading rails for $GILD aren't live yet — the Buy CTA is a "Coming
 * Soon" placeholder mirroring the sidebar's Buy Token button, so the
 * surface tells the user the right story before the rail ships.
 */

const WINDOW_OPTIONS = [
    { id: "1h", label: "1H" },
    { id: "5h", label: "5H" },
    { id: "7d", label: "7D" },
    { id: "30d", label: "30D" },
    { id: "all", label: "All" }
];

const ZONE_TONE = {
    Discount: "bg-[#22C55E]/20 text-[#22C55E] ring-[#22C55E]/50",
    Neutral: "bg-[#FBC206]/20 text-[#FBC206] ring-[#FBC206]/50",
    Premium: "bg-[#C93030]/20 text-[#ff8a8a] ring-[#C93030]/50"
};

// How fast the live tail polls. 5s on the headline page keeps the
// "Live" feel without hammering the RPC — the underlying chain reads
// (spot + intrinsic + zone) are all cheap view calls.
const LIVE_POLL_MS = 5_000;
// Chart series re-fetch cadence. Slower than the live tail because
// older points don't move; the loop is mostly to discover new
// snapshots the backend just appended.
const SERIES_POLL_MS = 30_000;

export default function MyTokens() {
    const { address } = useWallet();
    const [current, setCurrent] = useState(null);
    const [zone, setZone] = useState(null);
    const [series, setSeries] = useState(null);
    const [seriesLoading, setSeriesLoading] = useState(true);
    const [windowId, setWindowId] = useState("30d");
    const [liveTick, setLiveTick] = useState(0); // forces a re-render every poll

    const refreshHeader = useCallback(async () => {
        const [live, z] = await Promise.allSettled([fetchTvtCurrent(), fetchTvtZone()]);
        if (live.status === "fulfilled") setCurrent(live.value);
        if (z.status === "fulfilled") setZone(z.value);
        setLiveTick((t) => t + 1);
    }, []);

    const refreshSeries = useCallback(async () => {
        try {
            const data = await fetchTvtSeries({ windowId, samples: 90 });
            setSeries(data || null);
        } catch {
            setSeries(null);
        } finally {
            setSeriesLoading(false);
        }
    }, [windowId]);

    // Live tail poll — header cards + the synthetic "now" chart point
    // both depend on this.
    useEffect(() => {
        refreshHeader();
        const handle = setInterval(refreshHeader, LIVE_POLL_MS);
        return () => clearInterval(handle);
    }, [refreshHeader]);

    // Series refresh — drives the historical points. Resets the
    // loading flag whenever the window changes so the shimmer
    // appears during the gap.
    useEffect(() => {
        setSeriesLoading(true);
        refreshSeries();
        const handle = setInterval(refreshSeries, SERIES_POLL_MS);
        return () => clearInterval(handle);
    }, [refreshSeries]);

    const intrinsic = parseScaled(current?.treasuryValuePerToken);
    const spot = parseScaled(zone?.spotPrice);
    const zoneLabel = zone?.zone || "Calibrating";

    // Synthesise a "now" point from the live tail and pin it to the
    // chart. Without this the chart's right edge would lag by up to
    // SERIES_POLL_MS / 30s; the synthetic point makes the curve
    // visibly ticking while the slower poll catches up.
    const livePoint = useMemo(() => {
        if (intrinsic == null && spot == null) return null;
        return {
            // Chain seconds so this synthetic "now" sample aligns with the
            // chain-time observedAt timestamps on the historical series points.
            timestamp: chainNowSec(),
            tvt: intrinsic,
            marketPrice: spot
        };
    }, [intrinsic, spot, liveTick]); // eslint-disable-line react-hooks/exhaustive-deps

    const chart = useMemo(
        () => buildChart(series?.items || [], livePoint),
        [series, livePoint]
    );

    return (
        <div className="flex flex-col gap-4">
            <header className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] p-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#e6c985]/70">
                            Protocol Token
                        </div>
                        <h1 className="mt-1 font-manrope text-[26px] font-bold text-[#e6c985]">
                            $GILD
                        </h1>
                        <p className="mt-1 max-w-[640px] text-[11.5px] leading-snug text-[#e6c985]/80">
                            $GILD is the protocol-backed token. Every Term Deposit routes
                            a slice of its principal into Treasury-funded GIL buys,
                            growing the Treasury value backing each token (TVG).
                        </p>
                    </div>
                    <BuyTokenCta />
                </div>
            </header>

            <div className="grid gap-3 sm:grid-cols-3">
                <PriceCard
                    label="Spot Price"
                    value={spot != null ? formatUsd(spot) : "—"}
                    sub="GIL / USDC on Uniswap v2"
                    live
                />
                <PriceCard
                    label="Intrinsic Value (TVG)"
                    value={intrinsic != null ? formatUsd(intrinsic) : "—"}
                    sub="Treasury USDC / Circulating Supply"
                    live
                />
                <ZoneCard label={zoneLabel} />
            </div>

            <section className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <h3 className="font-manrope text-[13px] font-bold text-[#e6c985]">
                            $GILD Price History
                        </h3>
                        <LiveBadge />
                    </div>
                    <WindowPicker windowId={windowId} onChange={setWindowId} />
                </div>

                <div className="mt-3">
                    {seriesLoading && !chart ? (
                        <Shimmer width="100%" height="15rem" />
                    ) : !chart ? (
                        <EmptyChart />
                    ) : (
                        <InteractiveChart chart={chart} />
                    )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[10.5px] text-[#e6c985]/80">
                    <LegendDot color="#FBC206" label="Intrinsic Value (TVG)" />
                    {chart?.hasSpot ? (
                        <LegendDot color="#22C55E" label="Spot Price" />
                    ) : null}
                    <span className="text-[#e6c985]/55">
                        · updates every {LIVE_POLL_MS / 1000}s
                    </span>
                </div>
            </section>

            {!address ? (
                <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] px-4 py-3 text-center text-[11.5px] text-[#e6c985]/75">
                    Connect a wallet to track your token balance once the Buy rail goes live.
                </div>
            ) : null}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Header cards                                                              */
/* -------------------------------------------------------------------------- */

function PriceCard({ label, value, sub, live = false }) {
    return (
        <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
            <div className="flex items-center justify-between">
                <div className="text-[9.5px] font-bold uppercase tracking-widest text-[#e6c985]/70">
                    {label}
                </div>
                {live ? <PulseDot color="#22C55E" /> : null}
            </div>
            <div className="mt-1 font-manrope text-[22px] font-bold text-white">
                {value}
            </div>
            <div className="mt-0.5 text-[10px] text-[#e6c985]/60">{sub}</div>
        </div>
    );
}

function ZoneCard({ label }) {
    const tone = ZONE_TONE[label] || "bg-white/10 text-[#e6c985]/70 ring-white/20";
    return (
        <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
            <div className="text-[9.5px] font-bold uppercase tracking-widest text-[#e6c985]/70">
                Market Zone
            </div>
            <div className="mt-2 inline-flex items-center">
                <span
                    className={`rounded-full px-3 py-1 text-[12px] font-bold uppercase tracking-widest ring-1 ${tone}`}
                >
                    {label}
                </span>
            </div>
            <div className="mt-2 text-[10px] text-[#e6c985]/60">
                Spot relative to TVG — drives the §F price-band response.
            </div>
        </div>
    );
}

function BuyTokenCta() {
    return (
        <button
            type="button"
            disabled
            title="Coming Soon"
            className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-white/25 px-4 py-2 text-[12px] font-semibold text-white/55 cursor-not-allowed"
        >
            Buy $GILD
            <span className="rounded-full bg-[#FBC206] px-1.5 py-[1px] text-[8px] font-bold uppercase tracking-widest text-[#1D3E29]">
                Soon
            </span>
        </button>
    );
}

function LiveBadge() {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-[#22C55E]/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#22C55E] ring-1 ring-[#22C55E]/40">
            <PulseDot color="#22C55E" size={6} />
            Live
        </span>
    );
}

function PulseDot({ color = "#22C55E", size = 8 }) {
    // Two stacked dots: the inner solid one + an outer ping that
    // scales/fades to give the "pulse" effect. Pure CSS, no JS timer.
    return (
        <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
            <span
                className="absolute inset-0 animate-ping rounded-full opacity-75"
                style={{ background: color }}
            />
            <span
                className="relative inline-block rounded-full"
                style={{ width: size, height: size, background: color }}
            />
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*  Chart (interactive)                                                       */
/* -------------------------------------------------------------------------- */

function WindowPicker({ windowId, onChange }) {
    return (
        <div className="inline-flex overflow-hidden rounded-md ring-1 ring-[#bfb67b]">
            {WINDOW_OPTIONS.map((opt, idx) => {
                const active = opt.id === windowId;
                return (
                    <button
                        key={opt.id}
                        type="button"
                        onClick={() => onChange(opt.id)}
                        className={`px-3 py-1 text-[10.5px] font-semibold uppercase tracking-widest transition-colors ${
                            active
                                ? "bg-[#FBC206] text-[#10281b]"
                                : "text-[#e6c985] hover:bg-white/10"
                        } ${idx > 0 ? "border-l border-[#bfb67b]" : ""}`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function LegendDot({ color, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: color }}
            />
            {label}
        </span>
    );
}

function EmptyChart() {
    return (
        <div className="flex h-[15rem] items-center justify-center rounded-[10px] bg-white/5 ring-1 ring-[#bfb67b]/40 text-[11px] text-[#e6c985]/60">
            Not enough snapshots yet — chart appears once the monitor has collected
            two or more price observations.
        </div>
    );
}

/**
 * Hover-aware chart. Tracks mouse X position, finds the nearest data
 * point, and renders a crosshair + tooltip. Smooth curves use Catmull-
 * Rom-to-Bézier conversion so the line reads as continuous rather than
 * zig-zagging between discrete samples.
 */
function InteractiveChart({ chart }) {
    const svgRef = useRef(null);
    const [hoverIdx, setHoverIdx] = useState(null);

    function onMouseMove(event) {
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 700; // viewBox width
        const idx = findClosestIndex(chart.points, x);
        setHoverIdx(idx);
    }

    function onMouseLeave() {
        setHoverIdx(null);
    }

    const hover = hoverIdx != null ? chart.points[hoverIdx] : null;
    const lastPoint = chart.points[chart.points.length - 1];

    return (
        <div className="relative">
            <svg
                ref={svgRef}
                viewBox="0 0 700 260"
                className="w-full h-[15rem]"
                role="img"
                aria-label="$GILD price history"
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
            >
                <defs>
                    <linearGradient id="tvt-area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FBC206" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#FBC206" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="spot-area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22C55E" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                    </linearGradient>
                </defs>

                {/* Grid */}
                {chart.yGrid.map((row) => (
                    <line
                        key={`grid-${row.label}`}
                        x1={48}
                        x2={680}
                        y1={row.y}
                        y2={row.y}
                        stroke="#bfb67b"
                        strokeOpacity={0.12}
                        strokeDasharray="3 5"
                    />
                ))}

                {/* TVT area + smooth line */}
                <path d={chart.tvtAreaD} fill="url(#tvt-area)" />
                <path
                    d={chart.tvtLineD}
                    stroke="#FBC206"
                    strokeWidth={2.2}
                    fill="none"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />

                {/* Spot smooth line (when available) */}
                {chart.hasSpot ? (
                    <>
                        <path d={chart.spotAreaD} fill="url(#spot-area)" />
                        <path
                            d={chart.spotLineD}
                            stroke="#22C55E"
                            strokeWidth={2}
                            fill="none"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                        />
                    </>
                ) : null}

                {/* Y-axis labels */}
                {chart.yGrid.map((row) => (
                    <text
                        key={`y-${row.label}`}
                        x={8}
                        y={row.y + 3}
                        fill="#e6c985"
                        fillOpacity={0.7}
                        fontSize={9}
                        fontFamily="Manrope, sans-serif"
                    >
                        {row.label}
                    </text>
                ))}

                {/* X-axis labels */}
                {chart.xLabels.map((label, i) => (
                    <text
                        key={`x-${i}`}
                        x={label.x}
                        y={252}
                        fill="#e6c985"
                        fillOpacity={0.6}
                        fontSize={9}
                        fontFamily="Manrope, sans-serif"
                        textAnchor={i === 0 ? "start" : i === chart.xLabels.length - 1 ? "end" : "middle"}
                    >
                        {label.label}
                    </text>
                ))}

                {/* Live "now" marker — the last point pulses to signal
                    the curve is live. Two concentric circles: a static
                    inner dot + an outer halo with a fading animation. */}
                {lastPoint ? (
                    <g>
                        <circle
                            cx={lastPoint.x}
                            cy={lastPoint.tvtY}
                            r={9}
                            fill="#FBC206"
                            opacity={0.25}
                        >
                            <animate
                                attributeName="r"
                                values="6;14;6"
                                dur="2s"
                                repeatCount="indefinite"
                            />
                            <animate
                                attributeName="opacity"
                                values="0.45;0;0.45"
                                dur="2s"
                                repeatCount="indefinite"
                            />
                        </circle>
                        <circle
                            cx={lastPoint.x}
                            cy={lastPoint.tvtY}
                            r={3.5}
                            fill="#FBC206"
                            stroke="#10281b"
                            strokeWidth={1.5}
                        />
                        {chart.hasSpot && lastPoint.spotY != null ? (
                            <>
                                <circle
                                    cx={lastPoint.x}
                                    cy={lastPoint.spotY}
                                    r={9}
                                    fill="#22C55E"
                                    opacity={0.25}
                                >
                                    <animate
                                        attributeName="r"
                                        values="6;14;6"
                                        dur="2s"
                                        repeatCount="indefinite"
                                    />
                                    <animate
                                        attributeName="opacity"
                                        values="0.45;0;0.45"
                                        dur="2s"
                                        repeatCount="indefinite"
                                    />
                                </circle>
                                <circle
                                    cx={lastPoint.x}
                                    cy={lastPoint.spotY}
                                    r={3.5}
                                    fill="#22C55E"
                                    stroke="#10281b"
                                    strokeWidth={1.5}
                                />
                            </>
                        ) : null}
                    </g>
                ) : null}

                {/* Hover crosshair + point markers */}
                {hover ? (
                    <g>
                        <line
                            x1={hover.x}
                            x2={hover.x}
                            y1={24}
                            y2={232}
                            stroke="#e6c985"
                            strokeOpacity={0.35}
                            strokeDasharray="2 3"
                        />
                        <circle cx={hover.x} cy={hover.tvtY} r={4} fill="#FBC206" stroke="#10281b" strokeWidth={1.5} />
                        {chart.hasSpot && hover.spotY != null ? (
                            <circle cx={hover.x} cy={hover.spotY} r={4} fill="#22C55E" stroke="#10281b" strokeWidth={1.5} />
                        ) : null}
                    </g>
                ) : null}
            </svg>

            {hover ? <HoverTooltip hover={hover} chart={chart} /> : null}
        </div>
    );
}

function HoverTooltip({ hover, chart }) {
    // Position the tooltip relative to the SVG's data-space X. The svg
    // uses viewBox=700 → percent-based positioning maps back to the
    // rendered DOM width without re-measuring on every move.
    const pct = (hover.x / 700) * 100;
    const flipLeft = pct > 70;
    return (
        <div
            className={`pointer-events-none absolute top-2 ${flipLeft ? "right-[var(--off)]" : "left-[var(--off)]"} rounded-[8px] bg-[#10281b]/95 ring-1 ring-[#bfb67b] px-2.5 py-1.5 text-[10px] text-white shadow-lg`}
            style={{ "--off": `${flipLeft ? 100 - pct : pct}%` }}
        >
            <div className="text-[9.5px] text-[#e6c985]/75">{formatHoverDate(hover.timestamp)}</div>
            <div className="mt-0.5 flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#FBC206" }} />
                <span>TVG {formatUsd(hover.tvtValue)}</span>
            </div>
            {chart.hasSpot && hover.spotValue != null ? (
                <div className="mt-0.5 flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#22C55E" }} />
                    <span>Spot {formatUsd(hover.spotValue)}</span>
                </div>
            ) : null}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Chart builder                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Builds the chart geometry. Returns `points[]` of pixel positions +
 * smooth Bézier path strings for both TVT and Spot lines. The synthetic
 * live `tail` point is appended (if present) so the curve runs all the
 * way to "now" instead of stopping at the latest snapshot.
 */
function buildChart(items, tail) {
    let merged = [...(items || [])].sort((a, b) => a.timestamp - b.timestamp);
    if (tail) {
        // Skip the tail if a snapshot is already at-or-past the tail's
        // timestamp (e.g. server clock ahead, or a sub-second poll
        // raced us). Otherwise the synthetic point would land "before"
        // the last real one and zig-zag the line.
        const lastReal = merged[merged.length - 1];
        const tailHasValue =
            (tail.tvt != null && tail.tvt > 0) ||
            (tail.marketPrice != null && tail.marketPrice > 0);
        if (tailHasValue && (!lastReal || tail.timestamp > lastReal.timestamp)) {
            merged = [
                ...merged,
                {
                    timestamp: tail.timestamp,
                    tvt: tail.tvt != null ? String(BigInt(Math.floor(tail.tvt * 1e18))) : "0",
                    marketPrice:
                        tail.marketPrice != null
                            ? String(BigInt(Math.floor(tail.marketPrice * 1e18)))
                            : "0"
                }
            ];
        }
    }
    if (merged.length < 2) return null;

    const left = 48;
    const right = 680;
    const top = 24;
    const bottom = 224;

    const tvtVals = merged.map((p) => normaliseScaled(p.tvt));
    const spotVals = merged.map((p) => normaliseScaled(p.marketPrice));
    const hasSpot = spotVals.some((v) => v != null && v > 0);

    const valueDomain = [
        ...tvtVals.filter((v) => v != null && Number.isFinite(v) && v > 0),
        ...(hasSpot ? spotVals.filter((v) => v != null && v > 0) : [])
    ];
    if (valueDomain.length < 2) return null;

    const rawMin = Math.min(...valueDomain);
    const rawMax = Math.max(...valueDomain);
    // Pad the range slightly so the curve never kisses the chart edge.
    const pad = (rawMax - rawMin) * 0.08 || rawMax * 0.05 || 1e-6;
    const yMin = Math.max(0, rawMin - pad);
    const yMax = rawMax + pad;
    const span = yMax - yMin || 1;

    const n = merged.length;
    const xFor = (i) => left + ((right - left) / Math.max(n - 1, 1)) * i;
    const yFor = (v) => bottom - ((v - yMin) / span) * (bottom - top);

    const points = merged.map((p, i) => ({
        i,
        timestamp: p.timestamp,
        x: xFor(i),
        tvtValue: tvtVals[i],
        spotValue: hasSpot ? spotVals[i] : null,
        tvtY: yFor(tvtVals[i] ?? yMin),
        spotY: hasSpot && spotVals[i] != null && spotVals[i] > 0 ? yFor(spotVals[i]) : null
    }));

    const tvtLineD = catmullRomToBezier(points.map((p) => [p.x, p.tvtY]));
    const tvtAreaD = `${tvtLineD} L ${right},${bottom} L ${left},${bottom} Z`;

    let spotLineD = "";
    let spotAreaD = "";
    if (hasSpot) {
        const spotPts = points
            .filter((p) => p.spotY != null)
            .map((p) => [p.x, p.spotY]);
        if (spotPts.length >= 2) {
            spotLineD = catmullRomToBezier(spotPts);
            const firstX = spotPts[0][0];
            const lastX = spotPts[spotPts.length - 1][0];
            spotAreaD = `${spotLineD} L ${lastX},${bottom} L ${firstX},${bottom} Z`;
        }
    }

    // Y grid: 4 evenly spaced rows so values are legible without
    // crowding. Min/max land on the rounded value labels for honesty.
    const yGrid = [0, 0.25, 0.5, 0.75, 1].map((t) => {
        const value = yMin + span * (1 - t);
        return { label: formatUsd(value), y: top + (bottom - top) * t };
    });

    // X labels: 3 ticks (start, middle, end) — keeps the strip clean
    // at every window. Long windows show dates; sub-day windows
    // collapse to time-of-day.
    const firstTs = merged[0].timestamp;
    const lastTs = merged[merged.length - 1].timestamp;
    const midTs = Math.floor((firstTs + lastTs) / 2);
    const useTime = lastTs - firstTs < 60 * 60 * 24; // < 1 day
    const xLabels = [
        { label: useTime ? formatTime(firstTs) : formatDate(firstTs), x: left },
        { label: useTime ? formatTime(midTs) : formatDate(midTs), x: (left + right) / 2 },
        { label: useTime ? formatTime(lastTs) : formatDate(lastTs), x: right }
    ];

    return {
        points,
        tvtLineD,
        tvtAreaD,
        spotLineD,
        spotAreaD,
        hasSpot: hasSpot && spotLineD !== "",
        yGrid,
        xLabels
    };
}

/**
 * Catmull-Rom-to-Bézier path builder. Takes an ordered list of [x, y]
 * pairs and returns an SVG path string with smooth cubic Bézier
 * segments — visually continuous, never overshoots the data points.
 * Standard tension 0.5; tweak if curves feel too tight/loose.
 */
function catmullRomToBezier(pts) {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M ${pts[0][0]},${pts[0][1]}`;
    const tension = 0.5;
    let d = `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1[0] + ((p2[0] - p0[0]) / 6) * tension * 2;
        const c1y = p1[1] + ((p2[1] - p0[1]) / 6) * tension * 2;
        const c2x = p2[0] - ((p3[0] - p1[0]) / 6) * tension * 2;
        const c2y = p2[1] - ((p3[1] - p1[1]) / 6) * tension * 2;
        d += ` C ${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
    }
    return d;
}

function findClosestIndex(points, x) {
    if (!points || points.length === 0) return null;
    let bestIdx = 0;
    let bestDist = Math.abs(points[0].x - x);
    for (let i = 1; i < points.length; i++) {
        const d = Math.abs(points[i].x - x);
        if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
        }
    }
    return bestIdx;
}

/* -------------------------------------------------------------------------- */
/*  Parsing + formatting                                                      */
/* -------------------------------------------------------------------------- */

function parseScaled(raw) {
    if (raw == null) return null;
    try {
        const value = BigInt(raw);
        if (value === 0n) return null;
        return Number(value) / 1e18;
    } catch {
        return null;
    }
}

function normaliseScaled(raw) {
    if (raw == null) return null;
    try {
        const value = BigInt(raw);
        return Number(value) / 1e18;
    } catch {
        return null;
    }
}

function formatUsd(value) {
    if (value == null || !Number.isFinite(value)) return "—";
    if (value >= 1000) return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (value >= 1) return `$${value.toFixed(2)}`;
    if (value >= 0.01) return `$${value.toFixed(4)}`;
    if (value > 0) return `$${value.toExponential(2)}`;
    return "$0";
}

function formatDate(timestamp) {
    if (!timestamp) return "—";
    const d = new Date(timestamp * 1000);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatTime(timestamp) {
    if (!timestamp) return "—";
    const d = new Date(timestamp * 1000);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function formatHoverDate(timestamp) {
    if (!timestamp) return "—";
    const d = new Date(timestamp * 1000);
    return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}
